import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import EstaSemanaCard from '../components/EstaSemanaCard';
import { cn, formatDateShort, getLevelColor, TAREAS } from '../lib/utils';
import PagoCountdownCard from '../components/PagoCountdownCard';
import DashboardCommsCards from '../components/DashboardCommsCards';
import CreditosGratuitosCard from '../components/CreditosGratuitosCard';
import ContingencyBanner from '../components/ContingencyBanner';
import WhatsAppPremiumCard from '../components/WhatsAppPremiumCard';
import NivelEstimadoBadge from '../components/NivelEstimadoBadge';
import DashboardParvularia from './DashboardParvularia';
import { isParvularia } from '../lib/parvularia-data';
import { useIsParvularia } from '../context/TesterModalityContext';
import TesterModalitySwitcher from '../components/TesterModalitySwitcher';
import {
    MessageSquare,
    FileText,
    BarChart3,
    ChevronRight,
    Loader2,
    AlertCircle,
    BookOpen,
    TrendingUp,
    Building2,
    ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

export default function Dashboard() {
    const { user, profile } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    // Discriminador Parvularia con override tester (calculado antes del early return)
    const esParvularia = useIsParvularia(profile?.perfil);

    useEffect(() => {
        // Skip loadStats when Parvularia — su Dashboard es autónomo
        if (esParvularia) return;
        loadStats();
    }, [esParvularia]);

    // Fase A · Parvularia: los usuarios con perfil "Educación Parvularia" ven
    // un dashboard exclusivo. El resto (Básica/Media/Generalista/Diferencial)
    // mantienen el dashboard actual sin cambios.
    if (esParvularia) {
        return <DashboardParvularia />;
    }

    const loadStats = async () => {
        try {
            const response = await dashboardAPI.getStats();
            setStats(response.data);
        } catch (error) {
            console.error('Error loading stats:', error);
            toast.error('Error al cargar estadísticas');
        } finally {
            setLoading(false);
        }
    };

    const calcularProgreso = () => {
        if (!stats) return 0;
        const total = 22; // Total de indicadores
        const trabajados = stats.indicadores_trabajados || 0;
        return Math.round((trabajados / total) * 100);
    };

    const getNivelCount = (nivel) => {
        return stats?.niveles?.[nivel] || 0;
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex h-96 items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in">
                {/* Welcome Header */}
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h1 className="font-heading text-2xl font-bold text-slate-900 md:text-3xl">
                            Hola, {user?.nombre?.split(' ')[0]}
                        </h1>
                        <p className="mt-1 text-slate-500">
                            Continúa trabajando en tu portafolio docente
                        </p>
                    </div>
                    <Button
                        onClick={() => navigate('/chat')}
                        className="gap-2"
                        data-testid="dashboard-new-chat"
                    >
                        <MessageSquare className="h-4 w-4" />
                        Nueva Consulta
                    </Button>
                </div>

                {/* Contador urgencia: 8 ago 2026 (activación del pago) */}
                <PagoCountdownCard />

                {/* Selector Modalidad para testers QA (whitelist backend) */}
                <TesterModalitySwitcher />

                {/* Ventana de contingencia global (si está activa) */}
                <ContingencyBanner />

                {/* CTA comercial WhatsApp — visible para no-Premium (visible sin scroll) */}
                <WhatsAppPremiumCard />

                {/* Fase 1 Comercial: banner trial + WhatsApp condicional + Comunidad Premium + Upgrade */}
                <DashboardCommsCards user={user} />

                {/* Fase 2 Comercial: saldo de créditos IA gratuitos */}
                <CreditosGratuitosCard />

                {/* Profile Alert */}
                {!profile?.perfil_completado && (
                    <Card className="border-amber-200 bg-amber-50">
                        <CardContent className="flex items-center gap-4 p-4">
                            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                            <div className="flex-1">
                                <p className="font-medium text-amber-800">Completa tu perfil</p>
                                <p className="text-sm text-amber-700">
                                    Vincula tu establecimiento desde Radar Docente para recibir orientación personalizada.
                                </p>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate('/perfil')}
                                className="border-amber-300 text-amber-700 hover:bg-amber-100"
                                data-testid="complete-profile-btn"
                            >
                                Completar
                            </Button>
                        </CardContent>
                    </Card>
                )}

                {/* Esta semana enfócate en… (mentor estratégico) */}
                <EstaSemanaCard />

                {/* Establecimiento Card */}
                {profile?.perfil?.establecimiento_data && (
                    <Card className="border-emerald-200 bg-emerald-50/50">
                        <CardContent className="flex items-center gap-4 p-4">
                            <Building2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                            <div className="flex-1">
                                <p className="font-medium text-emerald-900">
                                    {profile.perfil.establecimiento_data.nombre}
                                </p>
                                <p className="text-sm text-emerald-700">
                                    RBD: {profile.perfil.establecimiento_data.rbd} | {profile.perfil.establecimiento_data.comuna}, {profile.perfil.establecimiento_data.region}
                                </p>
                            </div>
                            <Badge className="bg-emerald-100 text-emerald-700">Radar Docente</Badge>
                            <a
                                href={process.env.REACT_APP_RADAR_DOCENTE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Button variant="ghost" size="sm" className="gap-1 text-emerald-700">
                                    <ExternalLink className="h-3 w-3" />
                                    Ver
                                </Button>
                            </a>
                        </CardContent>
                    </Card>
                )}

                {/* Stats Grid */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <Card className="card-hover">
                        <CardHeader className="pb-2">
                            <CardDescription className="flex items-center gap-2">
                                <MessageSquare className="h-4 w-4" />
                                Conversaciones
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <p className="font-heading text-3xl font-bold text-slate-900">
                                {stats?.conversaciones_totales || 0}
                            </p>
                        </CardContent>
                    </Card>

                    <Card className="card-hover">
                        <CardHeader className="pb-2">
                            <CardDescription className="flex items-center gap-2">
                                <FileText className="h-4 w-4" />
                                Borradores
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <p className="font-heading text-3xl font-bold text-slate-900">
                                {stats?.borradores_totales || 0}
                            </p>
                        </CardContent>
                    </Card>

                    <Card className="card-hover">
                        <CardHeader className="pb-2">
                            <CardDescription className="flex items-center gap-2">
                                <BookOpen className="h-4 w-4" />
                                Indicadores Trabajados
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <p className="font-heading text-3xl font-bold text-slate-900">
                                {stats?.indicadores_trabajados || 0}
                                <span className="text-lg font-normal text-slate-400">/18</span>
                            </p>
                        </CardContent>
                    </Card>

                    <Card className="card-hover">
                        <CardHeader className="pb-2">
                            <CardDescription className="flex items-center gap-2">
                                <TrendingUp className="h-4 w-4" />
                                Progreso General
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <p className="font-heading text-3xl font-bold text-slate-900">
                                {calcularProgreso()}%
                            </p>
                            <Progress value={calcularProgreso()} className="mt-2 h-2" />
                        </CardContent>
                    </Card>
                </div>

                {/* Levels Overview */}
                <Card>
                    <CardHeader>
                        <CardTitle className="font-heading text-lg">Distribución por Nivel</CardTitle>
                        <CardDescription>
                            Indicadores evaluados según nivel de logro
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                            {[
                                { nivel: 'Destacado', color: 'bg-indigo-500' },
                                { nivel: 'Competente', color: 'bg-emerald-500' },
                                { nivel: 'Básico', color: 'bg-amber-500' },
                                { nivel: 'Insuficiente', color: 'bg-rose-500' },
                            ].map(({ nivel, color }) => (
                                <div
                                    key={nivel}
                                    className="flex items-center gap-3 rounded-lg border border-slate-200 p-3"
                                >
                                    <div className={cn('h-3 w-3 rounded-full', color)} />
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-slate-900">{nivel}</p>
                                    </div>
                                    <span className="font-heading text-lg font-bold text-slate-900">
                                        {getNivelCount(nivel)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Tasks Grid */}
                <div>
                    <h2 className="mb-4 font-heading text-lg font-semibold text-slate-900">
                        Tareas del Portafolio
                    </h2>
                    <div className="grid gap-4 md:grid-cols-3">
                        {TAREAS.map((tarea, index) => (
                            <Card
                                key={tarea.id}
                                className={cn(
                                    'task-card cursor-pointer',
                                    `task-card-${index + 1}`
                                )}
                                onClick={() => navigate(`/tareas/${tarea.id}`)}
                                data-testid={`task-card-${index + 1}`}
                            >
                                <div className="flex items-start justify-between">
                                    <div>
                                        <h3 className="font-heading text-lg font-semibold text-slate-900">
                                            {tarea.nombre}
                                        </h3>
                                        <p className="mt-1 text-sm text-slate-500">
                                            {tarea.descripcion}
                                        </p>
                                    </div>
                                    <ChevronRight className="h-5 w-5 text-slate-400" />
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>

                {/* Recent Activity */}
                <div className="grid gap-6 lg:grid-cols-2">
                    {/* Recent Conversations */}
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle className="font-heading text-lg">Conversaciones Recientes</CardTitle>
                                <CardDescription>Tus últimas consultas con el asistente</CardDescription>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate('/chat')}
                            >
                                Ver todas
                            </Button>
                        </CardHeader>
                        <CardContent>
                            {stats?.conversaciones_recientes?.length > 0 ? (
                                <div className="space-y-3">
                                    {stats.conversaciones_recientes.slice(0, 3).map((conv) => (
                                        <div
                                            key={conv.id}
                                            className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer hover:bg-slate-50 transition-colors"
                                            onClick={() => navigate(`/chat/${conv.id}`)}
                                        >
                                            <MessageSquare className="h-4 w-4 text-slate-400" />
                                            <div className="flex-1 min-w-0">
                                                <p className="truncate font-medium text-slate-900 text-sm">
                                                    {conv.titulo}
                                                </p>
                                                <p className="text-xs text-slate-500">
                                                    {conv.tarea} - {conv.indicador}
                                                </p>
                                            </div>
                                            <span className="text-xs text-slate-400">
                                                {formatDateShort(conv.updated_at)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-center text-sm text-slate-500 py-8">
                                    Aún no tienes conversaciones
                                </p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Recent Drafts */}
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle className="font-heading text-lg">Borradores Recientes</CardTitle>
                                <CardDescription>Tus últimos borradores guardados</CardDescription>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate('/borradores')}
                            >
                                Ver todos
                            </Button>
                        </CardHeader>
                        <CardContent>
                            {stats?.borradores_recientes?.length > 0 ? (
                                <div className="space-y-3">
                                    {stats.borradores_recientes.slice(0, 3).map((draft) => (
                                        <div
                                            key={draft.id}
                                            className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer hover:bg-slate-50 transition-colors"
                                            onClick={() => navigate(`/borradores/${draft.id}`)}
                                        >
                                            <FileText className="h-4 w-4 text-slate-400" />
                                            <div className="flex-1 min-w-0">
                                                <p className="truncate font-medium text-slate-900 text-sm">
                                                    {draft.tarea} - {draft.indicador}
                                                </p>
                                                <p className="text-xs text-slate-500 truncate">
                                                    {draft.contenido?.slice(0, 50)}...
                                                </p>
                                            </div>
                                            <NivelEstimadoBadge nivel={draft.nivel_estimado} size="sm" />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-center text-sm text-slate-500 py-8">
                                    Aún no tienes borradores
                                </p>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </Layout>
    );
}
